import React, { useContext, useEffect, useState } from "react";
import { LoginContext } from "../../reactcontext/ReactContext";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FaChevronDown, FaUsers, FaShieldAlt, FaKey, FaPlus, FaList, FaUser } from "react-icons/fa";
import AddUser from "./form/AddUser";
import UserTableInit from "./tables/UserTableInit";
import UserDetails from "./UserDetails";
import RoleManagement from "./RoleManagement";
import PermissionManagement from "./PermissionManagement";

const Landing = () => {
  const [selected, setSelected] = useState({});
  
  const [showSecondTab, setShowSecondTab] = useState(false);
  const [expandedMenu, setExpandedMenu] = useState(null);
  const { loginInfo } = useContext(LoginContext);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(1);

  // Define tab content
  const allTabs = [
    { 
      id: "create-user",
      title: "Create User", 
      icon: <FaPlus />,
      content: <AddUser setActiveTab={setActiveTab} /> 
    },
    {
      id: "list-users",
      title: "Lists Users",
      icon: <FaList />,
      content: (
        <UserTableInit setActiveTab={setActiveTab} setSelected={setSelected} />
      ),
    },
    {
      id: "user-info",
      title: "User Info",
      icon: <FaUser />,
      content: (
        <UserDetails
          setActiveTab={setActiveTab}
          user={selected}
          setSelected={setSelected}
        />
      ),
    },
    {
      id: "role-management",
      title: "Role Management",
      icon: <FaShieldAlt />,
      content: <RoleManagement />,
      adminOnly: true,
    },
    {
      id: "permission-management",
      title: "Permission Management",
      icon: <FaKey />,
      content: <PermissionManagement />,
      adminOnly: true,
    },
  ];

  const tabContent = allTabs;

  const getMenuStructure = () => {
    return [
      tabContent[0], // Create User
      tabContent[1], // Lists Users
      tabContent[2], // User Info
      {
        id: "manage-users",
        title: "Manage Users",
        icon: <FaUsers />,
        isParent: true,
        adminOnly: true,
        submenu: [
          tabContent[3], // Role Management
          tabContent[4], // Permission Management
        ]
      },
    ];
  };

  const menuStructure = getMenuStructure();

  useEffect(() => {
    !loginInfo.login && navigate("/users");
  }, [loginInfo]);

  // Handle tab change
  const handleTabChange = (tabId) => {
    const index = tabContent.findIndex(t => t.id === tabId);
    
    if (index !== -1) {
      setActiveTab(index);
    }
  };

  const handleMenuToggle = (menuId) => {
    setExpandedMenu(expandedMenu === menuId ? null : menuId);
  };

  const renderMenuButtons = () => {
    return (
      <div className="flex flex-wrap gap-2 items-center">
        {menuStructure.map((menu) => {
          // Skip admin-only tabs if user is not admin
          if (menu.adminOnly && loginInfo.userDetails.role?.toLowerCase() !== "admin") {
            return null;
          }

          if (menu.isParent) {
            // Render parent menu with dropdown
            return (
              <div key={menu.id} className="relative group">
                <button
                  onClick={() => handleMenuToggle(menu.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all duration-200 ${
                    expandedMenu === menu.id
                      ? "bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg"
                      : "bg-white text-gray-700 border-2 border-gray-200 hover:border-blue-500 hover:shadow-md"
                  }`}
                >
                  {menu.icon}
                  {menu.title}
                  <FaChevronDown 
                    className={`text-xs transition-transform duration-200 ${
                      expandedMenu === menu.id ? "rotate-180" : ""
                    }`} 
                  />
                </button>

                {/* Dropdown Menu */}
                {expandedMenu === menu.id && (
                  <div className="absolute top-full left-0 mt-2 w-56 bg-white border-2 border-blue-200 rounded-lg shadow-xl z-50 overflow-hidden">
                    {menu.submenu.map((submenu) => (
                      <button
                        key={submenu.id}
                        onClick={() => {
                          const submenuIndex = tabContent.findIndex(t => t.id === submenu.id);
                          setActiveTab(submenuIndex);
                          handleMenuToggle(null);
                        }}
                        className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-all duration-150 ${
                          activeTab === tabContent.findIndex(t => t.id === submenu.id)
                            ? "bg-blue-100 text-blue-700 border-l-4 border-blue-600"
                            : "text-gray-700 hover:bg-gray-50 border-l-4 border-transparent"
                        }`}
                      >
                        <span className="text-lg">{submenu.icon}</span>
                        <span className="font-medium">{submenu.title}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          } else {
            // Render regular menu button
            const menuIndex = tabContent.findIndex(t => t.id === menu.id);
            return (
              <button
                key={menu.id}
                onClick={() => {
                  setActiveTab(menuIndex);
                  setExpandedMenu(null);
                }}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all duration-200 ${
                  activeTab === menuIndex
                    ? "bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg"
                    : "bg-white text-gray-700 border-2 border-gray-200 hover:border-blue-500 hover:shadow-md"
                }`}
              >
                {menu.icon}
                {menu.title}
              </button>
            );
          }
        })}
      </div>
    );
  };

  return (
    <div className="w-full bg-gray-50 flex h-full flex-col items-center justify-between relative gap-3">
      {/* Header */}
      <div className="w-full bg-white border-b-2 border-gray-200 px-6 py-5">
        <div className="mb-4">
          <Link to={"/dashboard"} className="text-blue-600 hover:text-blue-800 font-medium">
            Dashboard
          </Link>
          <span className="mx-2 text-gray-400">/</span>
          <span className="text-gray-700 font-medium">Users</span>
        </div>

        {/* Menu Navigation */}
        {loginInfo.userDetails?.role?.toLowerCase() === "admin" && (
          <div className="pt-3">
            {renderMenuButtons()}
          </div>
        )}
      </div>

      {/* Render active tab content */}
      <div className="w-full p-4 flex items-center justify-center h-full overflow-auto">
        {tabContent[activeTab]?.content || <div className="text-center text-gray-500">Select a menu option</div>}
      </div>
    </div>
  );
};

export default Landing;
